import {BaseDocumentFragment, Fragment, FragmentMapper, SectionFragment, TreeArray, UUID} from '@nh/angular-tses';
import {DepartureInformationType} from '../departure/departure-information/departure-information-type';
import {DASFragmentType} from './das-fragment-type';
import {DasSectionType} from './das-section-type';
import {DepartureInformationFragment} from './departure-information-fragment';

export type DepartureInformation = Record<string, string | number | boolean>;

/**
 * Departure Fragment data object for DAS. This fragment type extends from the abtract class {@link DocumentFragment}
 * and is the root document object known to the angular-tses library.
 *
 * @field revisionNo  {number} Latest revision number for the departure
 * @field information {Record<string, string | number | boolean>} Legacy DAS 2 fields not mapped to any DAS 3 fields
 */
export class DepartureFragment extends BaseDocumentFragment {
  public children: TreeArray<SectionFragment>;

  constructor(
    id: UUID,
    children: SectionFragment[],
    value: string,
    public revisionNo: number,
    public importedDeparture: boolean,
    public indexedDeparture: boolean,
    public information?: Record<string, string | number | boolean>
  ) {
    super(id, value, children);
  }

  /**
   * @inheritDoc
   */
  public split(offset: number): DepartureFragment {
    return null;
  }

  public getInformation(type: DepartureInformationType): string {
    if (this.children) {
      const informationSection: SectionFragment = this.children.find(
        (s) => s.sectionType === DasSectionType.DEPARTURE_INFORMATION
      ) as SectionFragment;

      if (informationSection) {
        const res: Fragment[] = informationSection.iterateDown(null, null, (f: Fragment) => {
          if (f.is(DASFragmentType.INFORMATION) && (f as DepartureInformationFragment).isInformationType(type)) {
            return f;
          }
        });
        return res.length ? res[0].value : void 0;
      }
    }
  }
}

/**
 * Deserialiser function for Departure Fragments.
 *
 * @param json {any}                 JSON object to deserialise
 * @returns    {DepartureFragment}   Deserialised Departure Fragment Object
 */
export function deserialiseDeparture(json: any): DepartureFragment {
  const id: UUID = UUID.orThrow(json.id);
  const children: SectionFragment[] = FragmentMapper.deserialiseArray(
    json.children instanceof Array ? json.children : []
  ) as SectionFragment[];
  const value: string = json.value;
  const revisionNo: number = json.revisionNo;
  const importedDeparture: boolean = json.importedDeparture;
  const indexedDeparture: boolean = json.indexedDeparture;

  return new DepartureFragment(id, children, value, revisionNo, importedDeparture, indexedDeparture, json.information);
}
